import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Package, AlertCircle, CheckCircle, Edit, Trash2, Search, X, Eye, Folder, Package2, Flame, Upload, Check, ArrowUpDown, MoreVertical } from 'lucide-react';
import MaterialCategoryForm from './MaterialCategoryForm.jsx';
import MaterialDetails from './MaterialDetails.jsx';
import MaterialForm from './MaterialForm.jsx';
import ExcelUpload from './ExcelUpload.jsx';
import Modal from '../Layout/Modal.jsx';
import AllergenBadge from '../Allergens/AllergenBadge.jsx';
import { materialsAPI, uploadAPI } from '../../utils/api';
import { calculateUnitCostWithVat } from '../../utils/calculations';
import { useVat } from '../../contexts/VatContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';

const MaterialCategories = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { vatPercentage } = useVat();
  const { formatCurrency } = useCurrency();
  const { theme, isDark } = useTheme();
  const [categories, setCategories] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  
  // Unified search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState('categories'); // 'categories' or 'materials'
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showMaterialDetails, setShowMaterialDetails] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);
  
  // Excel Import/Export and Bulk Delete state for Materials mode
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isBulkDeleteMode, setIsBulkDeleteMode] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Bulk Delete state for Categories mode
  const [isCategoryBulkDeleteMode, setIsCategoryBulkDeleteMode] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [showCategoryBulkDeleteConfirm, setShowCategoryBulkDeleteConfirm] = useState(false);
  const [showCategoryWarningModal, setShowCategoryWarningModal] = useState(false);
  const [categoryWarningMessage, setCategoryWarningMessage] = useState('');
  
  // Add New Material state for Materials mode
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  
  // Individual Material Delete state
  const [materialToDelete, setMaterialToDelete] = useState(null);
  const [showMaterialDeleteModal, setShowMaterialDeleteModal] = useState(false);
  
  // Sorting state
  const [sortOrder, setSortOrder] = useState('id'); // 'id' or 'alphabetical'
  const [materialSortOrder, setMaterialSortOrder] = useState('id'); // 'id' or 'alphabetical'
  
  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openCategoryDropdownId, setOpenCategoryDropdownId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);
  
  // Refresh material data when VAT changes
  useEffect(() => {
    console.log('VAT changed to', vatPercentage, '- refreshing material data');
    loadData();
  }, [vatPercentage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownId(null);
    };

    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdownId]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenCategoryDropdownId(null);
    };

    if (openCategoryDropdownId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openCategoryDropdownId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, materialsData] = await Promise.all([
        materialsAPI.getCategories(),
        materialsAPI.getAll()
      ]);
      
      // Sort categories to put default category first
      const sortedCategories = categoriesData.sort((a, b) => {
        // Check if either category is the default category
        const aIsDefault = a.id === 0 || a.name_en?.toLowerCase().includes('default') || a.name_en?.toLowerCase().includes('unnamed');
        const bIsDefault = b.id === 0 || b.name_en?.toLowerCase().includes('default') || b.name_en?.toLowerCase().includes('unnamed');
        
        if (aIsDefault && !bIsDefault) return -1; // a comes first
        if (!aIsDefault && bIsDefault) return 1;  // b comes first
        return 0; // both are default or both are not default, maintain original order
      });
      
      setCategories(sortedCategories);
      setAllMaterials(materialsData);
      
      // Calculate material counts for all categories
      recalculateMaterialCounts(materialsData);
    } catch (error) {
      console.error('Error loading data:', error);
      setErrorMessage('Failed to load material categories');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      if (categoryData.id) {
        // Update existing category
        const updatedCategory = await materialsAPI.updateCategory(categoryData.id, categoryData);
        
        // Preserve the material_count from the existing category if not provided in the response
        const existingCategory = categories.find(cat => cat.id === categoryData.id);
        const categoryWithCount = {
          ...updatedCategory,
          material_count: updatedCategory.material_count ?? existingCategory?.material_count ?? 0
        };
        
        setCategories(categories.map(category => 
          category.id === categoryData.id ? categoryWithCount : category
        ));
        setSuccessMessage(t('messages.categorySaved'));
      } else {
        // Add new category
        const newCategory = await materialsAPI.createCategory(categoryData);
        setCategories([...categories, newCategory]);
        setSuccessMessage(t('messages.categorySaved'));
      }
      
      setShowCategoryForm(false);
      setEditingCategory(null);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving category:', error);
      setErrorMessage('Failed to save category');
      setShowErrorModal(true);
    }
  };

  const handleCategoryClick = (categoryId) => {
    console.log('Navigating to category:', categoryId);
    navigate(`/materials/category/${categoryId}`);
  };

  const handleEditCategory = (category, event) => {
    event.stopPropagation(); // Prevent navigation when clicking edit
    setEditingCategory(category);
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = (category, event) => {
    event.stopPropagation(); // Prevent navigation when clicking delete
    
    // Check if category has materials
    if (category.material_count > 0) {
      setErrorMessage(t('messages.cannotDeleteCategoryContainsMaterials', { name: category.name_en, count: category.material_count }));
      setShowErrorModal(true);
      return;
    }
    
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      await materialsAPI.deleteCategory(categoryToDelete.id);
      setCategories(categories.filter(category => category.id !== categoryToDelete.id));
      setSuccessMessage(t('messages.categoryDeleted'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error deleting category:', error);
      if (error.message && error.message.includes('Cannot delete category that is being used in materials')) {
        setErrorMessage(t('messages.cannotDeleteCategoryUsedInMaterials'));
      } else {
        setErrorMessage(t('messages.failedDeleteCategory'));
      }
      setShowErrorModal(true);
    } finally {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  // Unified search functionality
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setSelectedSearchIndex(-1);
    
    if (!searchValue.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    // For both categories and materials, filtering is handled by filteredCategories and filteredMaterials
    setShowSearchResults(false);
  };

  const handleSearchKeyDown = (e) => {
    const currentItems = searchMode === 'categories' ? filteredCategories : filteredMaterials;
    if (currentItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSearchIndex(prev => 
          prev < currentItems.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSearchIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSearchIndex >= 0 && selectedSearchIndex < currentItems.length) {
          handleSearchResultClick(currentItems[selectedSearchIndex]);
        }
        break;
      case 'Escape':
        setSearchTerm('');
        setSelectedSearchIndex(-1);
        break;
    }
  };

  const handleSearchResultClick = (result) => {
    if (searchMode === 'materials') {
      setSelectedMaterial(result);
      setShowMaterialDetails(true);
      setSearchTerm('');
    } else {
      // For categories, navigate to the category
      handleCategoryClick(result.id);
      setSearchTerm('');
    }
  };

  const handleCategoryCardClick = (category) => {
    // Always navigate to category, regardless of search state
    console.log('Category card clicked:', category);
    handleCategoryClick(category.id);
    // Clear search term when navigating to category
    setSearchTerm('');
    setSelectedSearchIndex(-1);
  };

  const handleMaterialCardClick = (material) => {
    // Always show material details, regardless of search state
    setSelectedMaterial(material);
    setShowMaterialDetails(true);
  };

  const handleNavigateToMaterial = (material) => {
    // Find the category for this material
    const category = categories.find(cat => cat.id === material.category_id);
    if (category) {
      navigate(`/materials/category/${material.category_id}`);
    }
  };

  const handleSearchModeChange = (mode) => {
    setSearchMode(mode);
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
    setSelectedSearchIndex(-1);
  };

  // Excel Import/Export functionality for Materials mode
  const handleExcelUpload = async (uploadedData) => {
    setIsUploading(true);
    try {
      // First, collect all unique category names that need to be created
      const categoriesToCreate = new Map();
      for (const materialData of uploadedData) {
        if (materialData.categoryName && !materialData.categoryId) {
          const categoryName = materialData.categoryName.trim();
          if (!categoriesToCreate.has(categoryName)) {
            categoriesToCreate.set(categoryName, []);
          }
          categoriesToCreate.get(categoryName).push(materialData);
        }
      }

      // Create categories and store their IDs
      const categoryIdMap = new Map();
      const newCategories = [];
      for (const [categoryName, materials] of categoriesToCreate) {
        try {
          const newCategory = await materialsAPI.createCategory({
            name_en: categoryName,
            name_ar: categoryName // You might want to add Arabic name handling
          });
          categoryIdMap.set(categoryName, newCategory.id);
          newCategories.push(newCategory);
          
          // Update all materials that use this category
          materials.forEach(material => {
            material.categoryId = newCategory.id;
          });
        } catch (error) {
          console.error('Error creating category:', error);
          // Use default category if creation fails
          categoryIdMap.set(categoryName, 0);
          materials.forEach(material => {
            material.categoryId = 0;
          });
        }
      }

      // Update local categories state with newly created categories
      if (newCategories.length > 0) {
        setCategories(prev => [...prev, ...newCategories]);
      }

      // Now create all materials
      const createdMaterials = [];
      for (const materialData of uploadedData) {
        const newMaterial = await materialsAPI.create({
          name_en: materialData.nameEn,
          name_ar: materialData.nameAr,
          brand_name: materialData.brandName,
          main_unit: materialData.mainUnit,
          quantity_per_main_unit: materialData.quantityPerMainUnit,
          quantity_per_piece: materialData.quantityPerPiece,
          sub_unit: materialData.subUnit,
          // main_price field removed - using price_with_vat instead
          price_with_vat: materialData.priceWithVat,
          price_without_vat: materialData.priceWithoutVat,
          supplier: materialData.supplier,
          calories: materialData.calories,
          serving_size: materialData.servingSize,
          category_id: materialData.categoryId !== undefined ? materialData.categoryId : 0
        });

        createdMaterials.push(newMaterial);
        // Add to local state
        setAllMaterials(prev => [...prev, newMaterial]);
      }

      // Recalculate material counts after Excel import
      const updatedMaterials = [...allMaterials, ...createdMaterials];
      recalculateMaterialCounts(updatedMaterials);

      setSuccessMessage(t('materials.successfullyUploaded', { count: uploadedData.length }));
      setShowSuccessModal(true);
      setShowExcelUpload(false);
    } catch (error) {
      console.error('Error uploading materials:', error);
      setShowExcelUpload(false);
      const rawReason = error?.message?.trim();
      const reason = rawReason === 'Failed to create material'
        ? t('messages.failedCreateMaterial')
        : rawReason;
      setErrorMessage(
        reason
          ? t('messages.failedUploadMaterialsWithReason', { reason })
          : t('messages.failedUploadMaterials')
      );
      setShowErrorModal(true);
    } finally {
      setIsUploading(false);
    }
  };

  // Bulk delete functions for Materials mode
  const handleBulkDeleteMode = () => {
    setIsBulkDeleteMode(true);
    setSelectedMaterials(new Set());
  };

  const handleCancelBulkDelete = () => {
    setIsBulkDeleteMode(false);
    setSelectedMaterials(new Set());
  };

  const handleMaterialSelection = (materialId) => {
    const newSelected = new Set(selectedMaterials);
    if (newSelected.has(materialId)) {
      newSelected.delete(materialId);
    } else {
      newSelected.add(materialId);
    }
    setSelectedMaterials(newSelected);
  };

  const handleSelectAll = () => {
    const currentMaterials = filteredMaterials;
    if (selectedMaterials.size === currentMaterials.length) {
      setSelectedMaterials(new Set());
    } else {
      setSelectedMaterials(new Set(currentMaterials.map(material => material.id)));
    }
  };

  const handleBulkDelete = async () => {
    setConfirmAction(() => async () => {
      try {
        const selectedMaterialIds = Array.from(selectedMaterials);
        const results = await Promise.allSettled(
          selectedMaterialIds.map(materialId => materialsAPI.delete(materialId))
        );
        
        // Track successful and failed deletions
        const successfulDeletions = [];
        const failedDeletions = [];
        
        results.forEach((result, index) => {
          const materialId = selectedMaterialIds[index];
          const material = allMaterials.find(m => m.id === materialId);
          
          if (result.status === 'fulfilled') {
            successfulDeletions.push(material);
          } else {
            // Check if it's a "used in recipes" error
            const errorMessage = result.reason?.message || 'Unknown error';
            if (errorMessage.includes('Cannot delete material that is being used in recipes')) {
              failedDeletions.push({
                material,
                reason: 'used in recipes'
              });
            } else {
              failedDeletions.push({
                material,
                reason: 'other error'
              });
            }
          }
        });
        
        // Update materials list (remove successfully deleted ones) immediately
        const updatedMaterials = allMaterials.filter(material => 
          !successfulDeletions.some(deleted => deleted.id === material.id)
        );
        
        // Clean up images for successfully deleted materials in the background
        for (const material of successfulDeletions) {
          if (material?.image_url) {
            cleanupMaterialImage(material.image_url).catch(error => {
              console.error('Background cleanup failed:', error);
            });
          }
        }
        setAllMaterials(updatedMaterials);

        // Recalculate material counts
        recalculateMaterialCounts(updatedMaterials);
        
        // Clear selection and exit bulk delete mode
        setSelectedMaterials(new Set());
        setIsBulkDeleteMode(false);
        
        // Show appropriate message based on results
        if (successfulDeletions.length > 0 && failedDeletions.length === 0) {
          // All deletions successful
          setSuccessMessage(t('messages.bulkMaterialsDeleted', { count: successfulDeletions.length }));
          setShowSuccessModal(true);
        } else if (successfulDeletions.length > 0 && failedDeletions.length > 0) {
          // Partial success
          const usedInRecipes = failedDeletions.filter(f => f.reason === 'used in recipes');
          const otherErrors = failedDeletions.filter(f => f.reason === 'other error');
          
          let message = t('messages.bulkMaterialsDeletedPartial', { count: successfulDeletions.length });
          
          if (usedInRecipes.length > 0) {
            message += ` ${t('messages.bulkMaterialsBlockedInRecipes', { count: usedInRecipes.length })}`;
          }
          
          if (otherErrors.length > 0) {
            message += ` ${t('messages.bulkMaterialsOtherErrors', { count: otherErrors.length })}`;
          }
          
          setSuccessMessage(message);
          setShowSuccessModal(true);
        } else {
          // All deletions failed
          const usedInRecipes = failedDeletions.filter(f => f.reason === 'used in recipes');
          const otherErrors = failedDeletions.filter(f => f.reason === 'other error');
          
          let errorMsg = `${t('messages.bulkMaterialsDeleteFailedPrefix')} `;
          if (usedInRecipes.length > 0) {
            errorMsg += t('messages.bulkMaterialsInRecipes', { count: usedInRecipes.length });
          }
          if (otherErrors.length > 0) {
            errorMsg += ` ${t('messages.bulkMaterialsOtherFailed', { count: otherErrors.length })}`;
          }
          
          setErrorMessage(errorMsg);
          setShowErrorModal(true);
        }
      } catch (error) {
        console.error('Error in bulk delete operation:', error);
        setErrorMessage(t('messages.bulkDeleteUnexpected'));
        setShowErrorModal(true);
      }
    });
    setShowBulkDeleteConfirm(true);
  };

  // Category bulk delete functions
  const handleCategoryBulkDeleteMode = () => {
    setIsCategoryBulkDeleteMode(true);
    setSelectedCategories(new Set());
  };

  const handleCancelCategoryBulkDelete = () => {
    setIsCategoryBulkDeleteMode(false);
    setSelectedCategories(new Set());
  };

  const handleCategorySelection = (categoryId) => {
    // Prevent selection of default category (ID 0)
    if (categoryId === 0) {
      setCategoryWarningMessage("Default category cannot be deleted as it is essential for the system.");
      setShowCategoryWarningModal(true);
      return;
    }
    
    // Check if category contains materials
    const category = categories.find(cat => cat.id === categoryId);
    if (category && category.material_count > 0) {
      setCategoryWarningMessage(t('messages.cannotDeleteCategoryContainsMaterials', { name: category.name_en, count: category.material_count }));
      setShowCategoryWarningModal(true);
      return;
    }
    
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(categoryId)) {
      newSelected.delete(categoryId);
    } else {
      newSelected.add(categoryId);
    }
    setSelectedCategories(newSelected);
  };

  const handleSelectAllCategories = () => {
    // Get all categories except the default category (ID 0) and categories that have materials
    const selectableCategories = filteredCategories.filter(category => 
      category.id !== 0 && category.material_count === 0
    );
    
    console.log('Select All - Selectable categories:', selectableCategories);
    console.log('Select All - Current selected count:', selectedCategories.size);
    console.log('Select All - Selectable count:', selectableCategories.length);
    
    if (selectedCategories.size === selectableCategories.length) {
      setSelectedCategories(new Set());
    } else {
      setSelectedCategories(new Set(selectableCategories.map(category => category.id)));
    }
  };

  const handleCategoryBulkDelete = async () => {
    setConfirmAction(() => async () => {
      try {
        const selectedCategoryIds = Array.from(selectedCategories);
        const results = await Promise.allSettled(
          selectedCategoryIds.map(categoryId => materialsAPI.deleteCategory(categoryId))
        );
        
        // Track successful and failed deletions
        const successfulDeletions = [];
        const failedDeletions = [];
        
        results.forEach((result, index) => {
          const categoryId = selectedCategoryIds[index];
          const category = categories.find(c => c.id === categoryId);
          
          if (result.status === 'fulfilled') {
            successfulDeletions.push(category);
          } else {
            // Check if it's a "used in materials" error
            const errorMessage = result.reason?.message || 'Unknown error';
            if (errorMessage.includes('Cannot delete category that is being used in materials')) {
              failedDeletions.push({
                category,
                reason: 'used in materials'
              });
            } else {
              failedDeletions.push({
                category,
                reason: 'other error'
              });
            }
          }
        });
        
        // Update categories list (remove successfully deleted ones)
        setCategories(categories.filter(category => 
          !successfulDeletions.some(deleted => deleted.id === category.id)
        ));
        
        // Clear selection and exit bulk delete mode
        setSelectedCategories(new Set());
        setIsCategoryBulkDeleteMode(false);
        
        // Show appropriate message based on results
        if (successfulDeletions.length > 0 && failedDeletions.length === 0) {
          // All deletions successful
          setSuccessMessage(t('messages.bulkCategoriesDeleted', { count: successfulDeletions.length }));
          setShowSuccessModal(true);
        } else if (successfulDeletions.length > 0 && failedDeletions.length > 0) {
          // Partial success
          const usedInMaterials = failedDeletions.filter(f => f.reason === 'used in materials');
          const otherErrors = failedDeletions.filter(f => f.reason === 'other error');
          
          let message = t('messages.bulkCategoriesDeletedPartial', { count: successfulDeletions.length });
          
          if (usedInMaterials.length > 0) {
            message += ` ${t('messages.bulkCategoriesContainMaterials', { count: usedInMaterials.length })}`;
          }
          
          if (otherErrors.length > 0) {
            message += ` ${t('messages.bulkCategoriesOtherErrors', { count: otherErrors.length })}`;
          }
          
          setSuccessMessage(message);
          setShowSuccessModal(true);
        } else {
          // All deletions failed
          const usedInMaterials = failedDeletions.filter(f => f.reason === 'used in materials');
          const otherErrors = failedDeletions.filter(f => f.reason === 'other error');
          
          let errorMsg = `${t('messages.bulkCategoriesDeleteFailedPrefix')} `;
          if (usedInMaterials.length > 0) {
            errorMsg += t('messages.bulkCategoriesContainMaterialsShort', { count: usedInMaterials.length });
          }
          if (otherErrors.length > 0) {
            errorMsg += ` ${t('messages.bulkCategoriesOtherFailed', { count: otherErrors.length })}`;
          }
          
          setErrorMessage(errorMsg);
          setShowErrorModal(true);
        }
      } catch (error) {
        console.error('Error in category bulk delete operation:', error);
        setErrorMessage(t('messages.bulkDeleteUnexpected'));
        setShowErrorModal(true);
      }
    });
    setShowCategoryBulkDeleteConfirm(true);
  };

  // Helper function to recalculate material counts for all categories
  const recalculateMaterialCounts = (materialsList) => {
    const materialCounts = {};
    
    // Count materials per category
    materialsList.forEach(material => {
      const categoryId = material.category_id;
      materialCounts[categoryId] = (materialCounts[categoryId] || 0) + 1;
    });
    
    // Update categories with new counts
    setCategories(prev => prev.map(category => ({
      ...category,
      material_count: materialCounts[category.id] || 0
    })));
  };

  // Add New Material functionality for Materials mode
  const handleSaveMaterial = async (materialData) => {
    try {
      if (editingMaterial) {
        // Update existing material
        const updatedMaterial = await materialsAPI.update(editingMaterial.id, materialData);
        const updatedMaterials = allMaterials.map(material => 
          material.id === editingMaterial.id ? updatedMaterial : material
        );
        setAllMaterials(updatedMaterials);
        
        // Recalculate material counts
        recalculateMaterialCounts(updatedMaterials);
        
        setSuccessMessage(t('messages.materialSaved'));
      } else {
        // Add new material
        const newMaterial = await materialsAPI.create(materialData);
        const updatedMaterials = [...allMaterials, newMaterial];
        setAllMaterials(updatedMaterials);
        
        // Recalculate material counts
        recalculateMaterialCounts(updatedMaterials);
        
        setSuccessMessage(t('messages.materialSaved'));
      }
      
      setShowMaterialForm(false);
      setEditingMaterial(null);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving material:', error);
      setErrorMessage('Failed to save material');
      setShowErrorModal(true);
    }
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setShowMaterialForm(true);
  };

  const handleDeleteMaterial = (material) => {
    setMaterialToDelete(material);
    setShowMaterialDeleteModal(true);
  };

  // Function to extract public ID from Cloudinary URL
  const extractPublicIdFromUrl = (url) => {
    if (!url) return null;
    try {
      // Extract public ID from Cloudinary URL
      // Format: https://res.cloudinary.com/cloud_name/image/upload/v123456789/folder/image.jpg
      const match = url.match(/\/upload\/[^\/]+\/(.+)$/);
      if (match) {
        let publicId = match[1];
        // Remove file extension from public ID (Cloudinary public IDs don't include extensions)
        publicId = publicId.replace(/\.[^/.]+$/, '');
        return publicId;
      }
      return null;
    } catch (error) {
      console.error('Error extracting public ID from URL:', error);
      return null;
    }
  };

  // Function to clean up material image from Cloudinary
  const cleanupMaterialImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const imagePublicId = extractPublicIdFromUrl(imageUrl);
      if (imagePublicId) {
        console.log('🧹 Cleanup: Deleting material image:', imagePublicId);
        await uploadAPI.deleteImage(imagePublicId);
        console.log('✅ Cleanup: Material image cleaned up successfully');
      }
    } catch (error) {
      console.error('❌ Cleanup: Failed to cleanup material image:', error);
    }
  };

  const confirmDeleteMaterial = async () => {
    if (!materialToDelete) return;

    try {
      // Delete the material from database
      await materialsAPI.delete(materialToDelete.id);
      
      // Update UI immediately for responsive experience
      const updatedMaterials = allMaterials.filter(material => material.id !== materialToDelete.id);
      setAllMaterials(updatedMaterials);
      
      // Recalculate material counts
      recalculateMaterialCounts(updatedMaterials);
      
      setSuccessMessage(t('messages.materialDeleted'));
      setShowSuccessModal(true);
      
      // Clean up the material's image from Cloudinary in the background
      if (materialToDelete?.image_url) {
        cleanupMaterialImage(materialToDelete.image_url).catch(error => {
          console.error('Background cleanup failed:', error);
        });
      }
    } catch (error) {
      console.error('Error deleting material:', error);
      if (error.message && error.message.includes('Cannot delete material that is being used in recipes')) {
        setErrorMessage(t('messages.cannotDeleteMaterialUsedInRecipes'));
      } else {
        setErrorMessage(t('messages.failedDeleteMaterial'));
      }
      setShowErrorModal(true);
    } finally {
      setShowMaterialDeleteModal(false);
      setMaterialToDelete(null);
    }
  };



  // Sort and filter categories for display
  const getSortedCategories = (categoriesList) => {
    return categoriesList.sort((a, b) => {
      // Always put default category first
      const aIsDefault = a.id === 0 || a.name_en?.toLowerCase().includes('default') || a.name_en?.toLowerCase().includes('unnamed');
      const bIsDefault = b.id === 0 || b.name_en?.toLowerCase().includes('default') || b.name_en?.toLowerCase().includes('unnamed');
      
      if (aIsDefault && !bIsDefault) return -1;
      if (!aIsDefault && bIsDefault) return 1;
      
      // Then apply the selected sort order
      if (sortOrder === 'id') {
        return a.id - b.id;
      } else {
        // alphabetical
        return (a.name_en || '').localeCompare(b.name_en || '');
      }
    });
  };

  // Filter categories for display (always filter based on search term)
  const filteredCategories = getSortedCategories(
    categories.filter(category => 
      category.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.id?.toString().includes(searchTerm)
    )
  );

  // Sort and filter materials for display
  const getSortedMaterials = (materialsList) => {
    return materialsList.sort((a, b) => {
      if (materialSortOrder === 'id') {
        return a.id - b.id;
      } else {
        // alphabetical
        return (a.name_en || '').localeCompare(b.name_en || '');
      }
    });
  };

  // Filter materials for display (always filter based on search term)
  const filteredMaterials = getSortedMaterials(
    allMaterials.filter(material => 
      material.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.id?.toString().includes(searchTerm) ||
      material.brand_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-background text-foreground min-h-screen p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('nav.materials')}</h1>
          <p className="mt-2 text-muted-foreground">
                          {searchMode === 'materials' ? t('materials.allTitle') : t('materials.subtitle')}
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className={`flex justify-end mt-4 sm:mt-0 ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
          {searchMode === 'materials' ? (
            // Materials mode action buttons
            <>
              {isBulkDeleteMode ? (
                <>
                  <button
                    onClick={handleCancelBulkDelete}
                    className="px-4 py-2 text-sm font-medium text-card-foreground bg-secondary border border-border rounded-md hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring flex items-center justify-center"
                  >
                    <X className={`h-4 w-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSelectAll}
                    className="px-4 py-2 text-sm font-medium text-card-foreground bg-secondary border border-border rounded-md hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring flex items-center justify-center"
                  >
                    <Check className={`h-4 w-4 ${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                    {selectedMaterials.size === filteredMaterials.length ? t('common.deselectAll') : t('common.selectAll')}
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={selectedMaterials.size === 0}
                    className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive border border-destructive rounded-md hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('common.deleteSelected')} ({selectedMaterials.size})
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowMaterialForm(true)}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-foreground bg-primary border-2 border-primary rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                    title="Add new material"
                  >
                    <Plus className="h-4 w-4 mr-2" />
{t('materials.addNew')}
                  </button>
                  <button
                    onClick={() => setShowExcelUpload(true)}
                    className="px-3 py-2 text-sm font-medium text-card-foreground bg-secondary border-2 border-secondary rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                    title="Import materials from Excel or export existing materials"
                  >
                    <Upload className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleBulkDeleteMode}
                    className="px-3 py-2 text-sm font-medium text-destructive-foreground bg-destructive border-2 border-destructive rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                    title={t('common.bulkDelete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </>
          ) : (
            // Categories mode action buttons
            <>
              {isCategoryBulkDeleteMode ? (
                <>
                  <button
                    onClick={handleCancelCategoryBulkDelete}
                    className="px-4 py-2 text-sm font-medium text-card-foreground bg-secondary border border-border rounded-md hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring flex items-center justify-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSelectAllCategories}
                    className="px-4 py-2 text-sm font-medium text-card-foreground bg-secondary border border-border rounded-md hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring flex items-center justify-center"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    {selectedCategories.size > 0 && selectedCategories.size === filteredCategories.filter(category => 
                      category.id !== 0 && category.material_count === 0
                    ).length ? t('common.deselectAll') : t('common.selectAll')}
                  </button>
                  <button
                    onClick={handleCategoryBulkDelete}
                    disabled={selectedCategories.size === 0}
                    className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive border border-destructive rounded-md hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('common.deleteSelected')} ({selectedCategories.size})
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowCategoryForm(true)}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-foreground bg-primary border-2 border-primary rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                    title="Add new category"
                  >
                    <Plus className="h-4 w-4 mr-2" />
{t('categories.addNew')}
                  </button>
                  <button
                    onClick={() => setShowExcelUpload(true)}
                    className="px-3 py-2 text-sm font-medium text-card-foreground bg-secondary border-2 border-secondary rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                    title="Import materials from Excel or export existing materials"
                  >
                    <Upload className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleCategoryBulkDeleteMode}
                    className="px-3 py-2 text-sm font-medium text-destructive-foreground bg-destructive border-2 border-destructive rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
                    title={t('common.bulkDelete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Search Mode Toggle */}
      <div className="flex justify-start">
        <div className="flex bg-muted rounded-lg p-1">
          <button
            onClick={() => handleSearchModeChange('categories')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              searchMode === 'categories'
                ? 'bg-card text-primary shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Folder className="h-4 w-4" />
            {t('categories.title')}
          </button>
          <button
            onClick={() => handleSearchModeChange('materials')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              searchMode === 'materials'
                ? 'bg-card text-primary shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Package2 className="h-4 w-4" />
            {t('common.materials')}
          </button>
        </div>
      </div>

      {/* Search and Sort */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Search Input */}
        <div className="flex-1 max-w-lg">
          <div className="relative">
            <Search className={`absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none ${i18n.language === 'ar' ? 'right-3' : 'left-3'}`} />
            <input
              type="text"
              placeholder={
                searchMode === 'categories' 
                  ? t('common.searchByCategoryNameOrId')
                  : t('common.searchByMaterialNameOrId')
              }
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className={`w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${i18n.language === 'ar' ? 'pr-10' : 'pl-10'}`}
              style={i18n.language === 'ar' ? { paddingRight: '2.75rem' } : { paddingLeft: '2.75rem' }}
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedSearchIndex(-1);
                }}
                className={`absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground ${i18n.language === 'ar' ? 'left-3' : 'right-3'}`}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Sort Button */}
        <button
          onClick={(e) => {
            if (searchMode === 'categories') {
              setSortOrder(sortOrder === 'id' ? 'alphabetical' : 'id');
            } else {
              setMaterialSortOrder(materialSortOrder === 'id' ? 'alphabetical' : 'id');
            }
            e.target.blur(); // Remove focus after click
          }}
          className="inline-flex items-center justify-center h-10 px-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
          title={`Sort by ${searchMode === 'categories' ? (sortOrder === 'id' ? 'alphabetical' : 'ID') : (materialSortOrder === 'id' ? 'alphabetical' : 'ID')} order`}
        >
          <ArrowUpDown className="h-4 w-4" />
        </button>
      </div>



      {/* All Materials Display - Show when in materials mode */}
      {searchMode === 'materials' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-card-foreground">
              {t('common.allMaterials')}
            </h3>
            <div className="text-sm text-muted-foreground">
              {i18n.language === 'ar' ? `${t('common.materialsTotal')} ${filteredMaterials.length.toLocaleString()}` : `${filteredMaterials.length} ${t('common.materialsTotal')}`}
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
            {filteredMaterials.map((material) => {
              const category = categories.find(cat => cat.id === material.category_id);
                              const unitCost = calculateUnitCostWithVat(material, vatPercentage);
                console.log('Material', material.name_en, 'unit cost calculated with VAT', vatPercentage, ':', unitCost);
              return (
                                 <div
                   key={material.id}
                   onClick={() => !isBulkDeleteMode && handleMaterialCardClick(material)}
                   className={`group ${isBulkDeleteMode ? 'cursor-default' : 'cursor-pointer'} card-container`}
                 >
                  <div className={`bg-card rounded-2xl border border-border transition-all duration-300 transform overflow-hidden flex flex-col h-full ${
                    isBulkDeleteMode && selectedMaterials.has(material.id)
                      ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                      : 'hover:-translate-y-1'
                  }`}>
                    {/* Selection Checkbox */}
                    {isBulkDeleteMode && (
                      <div className="absolute top-3 right-3 z-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMaterialSelection(material.id);
                          }}
                           className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                             selectedMaterials.has(material.id)
                               ? 'bg-primary border-primary text-primary-foreground'
                               : 'bg-card border-border hover:border-border'
                           }`}
                        >
                          {selectedMaterials.has(material.id) && (
                            <Check className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    )}

                    {/* Actions Dropdown */}
                    {!isBulkDeleteMode && (
                      <div className="absolute top-3 right-3 z-10">
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(openDropdownId === material.id ? null : material.id);
                            }}
                            className="w-8 h-8 rounded-full border border-border hover:border-border flex items-center justify-center transition-all hover:bg-muted dark:hover:bg-muted bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]"
                          >
                            <MoreVertical className="h-4 w-4 text-muted-foreground" />
                          </button>
                          
                          {openDropdownId === material.id && (
                            <div className="absolute right-0 top-9 bg-card rounded-lg shadow-lg border border-border py-1 min-w-[120px] z-20">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditMaterial(material);
                                  setOpenDropdownId(null);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-card-foreground hover:bg-muted flex items-center gap-2"
                              >
                                <Edit className="h-4 w-4" />
                                {t('common.edit')}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteMaterial(material);
                                  setOpenDropdownId(null);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-muted flex items-center gap-2"
                              >
                                <Trash2 className="h-4 w-4" />
                                {t('common.delete')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Material Image Header */}
                    <div className="relative h-28 bg-input flex items-center justify-center overflow-hidden border-b border-border">
                      {material.image_url ? (
                        <img
                          src={material.image_url}
                          alt={material.name_en}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-card flex items-center justify-center">
                          <Package className="h-16 w-16 text-muted-foreground/70" />
                        </div>
                      )}
                      
                      {/* Material ID Badge */}
                      <div className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium text-muted-foreground bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]">
                        {i18n.language === 'ar' ? material.id.toLocaleString() : material.id}
                      </div>
                      
                      {/* Calories Badge */}
                      <div className="absolute bottom-3 right-3 px-2 py-1 rounded-full text-xs font-medium text-orange-600 flex items-center gap-1 bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]">
                        <Flame className="h-3 w-3 text-orange-500" />
                        {material.calories ? Math.round(material.calories) : '-'}kcal/{material.serving_size || '-'}
                      </div>
                      
                      {/* View Details Overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="bg-card px-4 py-2 rounded-lg text-sm font-medium text-card-foreground">
                          {t('common.clickToViewDetails')}
                        </div>
                      </div>
                    </div>
                    
                    {/* Content Section */}
                    <div className="p-3 space-y-2 flex-1">
                      {/* Material Names */}
                      <div className="text-center space-y-1 -mt-2 border-b border-border pb-2 -mx-3 px-3">
                        {i18n.language === 'ar' ? (
                          <>
                            {material.name_ar && (
                              <h3 className="font-bold text-card-foreground text-lg group-hover:text-primary transition-colors truncate font-arabic" title={material.name_ar}>
                                {material.name_ar}
                              </h3>
                            )}
                            <p className="text-sm text-muted-foreground truncate" title={material.name_en}>
                              {material.name_en}
                            </p>
                          </>
                        ) : (
                          <>
                            <h3 className="font-bold text-card-foreground text-lg group-hover:text-primary transition-colors truncate" title={material.name_en}>
                              {material.name_en}
                            </h3>
                            {material.name_ar && (
                              <p className="text-sm text-muted-foreground font-arabic truncate" title={material.name_ar}>
                                {material.name_ar}
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      {/* Allergen Section */}
                      <div className="h-[48px] flex justify-center items-center">
                        {material.allergens && material.allergens.length > 0 ? (
                          <AllergenBadge
                            allergens={material.allergens}
                            variant="warning"
                            size="xs"
                            showCount={true}
                            maxDisplay={2}
                          />
                        ) : (
                          <div className="text-xs text-muted-foreground">{t('common.noAllergens')}</div>
                        )}
                      </div>

                      
                      {/* Unit Cost */}
                      <div className="p-2 text-center -mx-3 bg-[#B3E8F2] dark:bg-[#2A4A5A]">
                        <div className="mb-1">
                          <span className="text-xs text-card-foreground font-medium uppercase tracking-wide">{t('materials.unitCost')}</span>
                        </div>
                        <div className="text-sm font-bold text-card-foreground">
                          {formatCurrency(unitCost)}
                        </div>
                        <div className="text-xs text-card-foreground">
                          {t('materials.includesVat')}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="px-3 pb-3 mt-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigateToMaterial(material);
                        }}
                        className="w-full px-3 py-2 text-sm font-medium text-muted-foreground bg-muted/50 dark:bg-[rgba(10,18,25,0.5)] hover:bg-muted dark:hover:bg-muted border border-border hover:border-border rounded-md transition-all duration-200 flex items-center justify-center"
                        title={`Go to ${i18n.language === 'ar' ? (category?.name_ar || 'Category') : (category?.name_en || 'Category')}`}
                      >
                        <Folder className="h-4 w-4 mr-2" />
                        <span className="truncate">{i18n.language === 'ar' ? (category?.name_ar || 'Category') : (category?.name_en || 'Category')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Empty state for materials */}
          {filteredMaterials.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-card-foreground mb-2">
                {t('materials.noMaterialsFound')}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t('materials.noMaterialsAddedYet')}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Categories Grid - Show when in categories mode */}
      {searchMode === 'categories' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-card-foreground">
              {t('common.allCategories')}
            </h3>
            <div className="text-sm text-muted-foreground">
              {i18n.language === 'ar' ? `${t('common.categoriesTotal')} ${filteredCategories.length.toLocaleString()}` : `${filteredCategories.length} ${t('common.categoriesTotal')}`}
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
           {filteredCategories.map((category) => (
             <div
               key={category.id}
               onClick={(e) => {
                 if (!isCategoryBulkDeleteMode) {
                   console.log('Click event triggered for category:', category.name_en);
                   e.stopPropagation();
                   handleCategoryCardClick(category);
                 }
               }}
               onMouseDown={(e) => {
                 if (!isCategoryBulkDeleteMode) {
                   console.log('Mouse down on category card:', category.name_en);
                 }
               }}
               className={`group ${isCategoryBulkDeleteMode ? 'cursor-default' : 'cursor-pointer'} category-card card-container`}
             >
               <div className={`bg-card rounded-xl border border-border transition-all duration-200 transform flex flex-col h-52 p-6 ${
                 isCategoryBulkDeleteMode && selectedCategories.has(category.id) && category.material_count === 0
                   ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                   : 'hover:-translate-y-1'
               }`}>
                 {/* Selection Checkbox */}
                 {isCategoryBulkDeleteMode && (
                   <div className="absolute top-3 right-3 z-10">
                     <button
                       onClick={(e) => {
                         e.stopPropagation();
                         handleCategorySelection(category.id);
                       }}
                       className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                         category.id === 0 || category.material_count > 0
                           ? 'bg-muted border-border opacity-50'
                           : selectedCategories.has(category.id)
                           ? 'bg-primary border-primary text-primary-foreground'
                           : 'bg-card border-border hover:border-border'
                       }`}
                       title={
                         category.id === 0 
                           ? "Default category cannot be deleted" 
                           : category.material_count > 0
                           ? `Click to see why this category cannot be deleted`
                           : t('common.selectCategoryForDeletion')
                       }
                     >
                       {selectedCategories.has(category.id) && (
                         <Check className="h-4 w-4" />
                       )}
                     </button>
                   </div>
                 )}

                 {/* Actions Dropdown */}
                 {!isCategoryBulkDeleteMode && (
                   <div className="absolute top-3 right-3 z-10">
                     <div className="relative">
                       <button
                         onClick={(e) => {
                           e.stopPropagation();
                           setOpenCategoryDropdownId(openCategoryDropdownId === category.id ? null : category.id);
                         }}
                         className="w-8 h-8 rounded-full border border-border hover:border-border flex items-center justify-center transition-all hover:bg-muted dark:hover:bg-muted bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]"
                       >
                         <MoreVertical className="h-4 w-4 text-muted-foreground" />
                       </button>
                       
                       {openCategoryDropdownId === category.id && (
                         <div className="absolute right-0 top-9 bg-card rounded-lg shadow-lg border border-border py-1 min-w-[120px] z-20">
                           <button
                             onClick={(e) => {
                               e.stopPropagation();
                               if (category.id !== 0) {
                                 handleEditCategory(category, e);
                               }
                               setOpenCategoryDropdownId(null);
                             }}
                             className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                               category.id === 0
                                 ? 'text-muted-foreground cursor-not-allowed'
                                 : 'text-card-foreground hover:bg-muted'
                             }`}
                             disabled={category.id === 0}
                           >
                             <Edit className="h-4 w-4" />
                             {t('common.edit')}
                           </button>
                           <button
                             onClick={(e) => {
                               e.stopPropagation();
                               if (category.id !== 0) {
                                 handleDeleteCategory(category, e);
                               }
                               setOpenCategoryDropdownId(null);
                             }}
                             className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                               category.id === 0
                                 ? 'text-muted-foreground cursor-not-allowed'
                                 : 'text-destructive hover:bg-muted'
                             }`}
                             disabled={category.id === 0}
                           >
                             <Trash2 className="h-4 w-4" />
                             {t('common.delete')}
                           </button>
                         </div>
                       )}
                     </div>
                   </div>
                 )}
                 {/* Category ID */}
                 <div className="absolute top-3 left-3">
                   <span className="text-xs text-muted-foreground px-2 py-1 rounded-full bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]">
                     {i18n.language === 'ar' ? category.id.toLocaleString() : category.id}
                   </span>
                 </div>
                
                 <div className="space-y-2 flex-1 pt-6">
                   {i18n.language === 'ar' ? (
                     <>
                       {category.name_ar && (
                         <h3 className="font-semibold text-card-foreground text-lg group-hover:text-primary transition-colors truncate font-arabic" title={category.name_ar}>
                           {category.name_ar}
                         </h3>
                       )}
                       <p className="text-sm text-muted-foreground truncate" title={category.name_en}>
                         {category.name_en}
                       </p>
                     </>
                   ) : (
                     <>
                       <h3 className="font-semibold text-card-foreground text-lg group-hover:text-primary transition-colors truncate" title={category.name_en}>
                         {category.name_en}
                       </h3>
                       {category.name_ar && (
                         <p className="text-sm text-muted-foreground font-arabic truncate" title={category.name_ar}>
                           {category.name_ar}
                         </p>
                       )}
                     </>
                   )}
                   
                   {/* Materials Count */}
                   <div className="text-sm text-muted-foreground mb-2">
                     {i18n.language === 'ar' ? `${(category.material_count || 0).toLocaleString()} مادة` : `${(category.material_count || 0)} materials`}
                   </div>
                   
                   <div className="border-t border-border my-2"></div>
                   <div className={`flex items-center text-sm text-muted-foreground ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
                     <span className="truncate">{t('common.clickToViewMaterials')}</span>
                     <svg className={`h-4 w-4 group-hover:translate-x-1 transition-transform flex-shrink-0 ${i18n.language === 'ar' ? 'ml-auto mr-2 group-hover:-translate-x-1' : 'ml-auto'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={i18n.language === 'ar' ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                     </svg>
                   </div>
                 </div>
               </div>
             </div>
           ))}
         </div>
        </div>
       )}

      {/* Empty State */}
      {searchMode === 'categories' && filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {searchTerm ? <Search className="h-8 w-8 text-gray-400" /> : <Package className="h-8 w-8 text-gray-400" />}
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No categories found' : 'No categories yet'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm 
              ? 'Try adjusting your search terms' 
              : 'Create your first material category to get started'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCategoryForm(true)}
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Category
            </button>
          )}
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <MaterialCategoryForm
          category={editingCategory}
          onSave={handleSaveCategory}
          onCancel={() => {
            setShowCategoryForm(false);
            setEditingCategory(null);
          }}
        />
      )}

      {/* Error Modal */}
      <Modal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)}>
        <div className="flex items-center mb-6">
          <AlertCircle className="h-6 w-6 text-destructive mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.error')}</h2>
        </div>
        <div className="mb-8">
          <p className="text-card-foreground">{errorMessage}</p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => setShowErrorModal(false)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
          >
            {t('common.ok')}
          </button>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <div className="flex items-center mb-6">
          <CheckCircle className="h-6 w-6 text-chart-3 mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.success')}</h2>
        </div>
        <div className="mb-8">
          <p className="text-card-foreground">{successMessage}</p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => setShowSuccessModal(false)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
          >
            {t('common.ok')}
          </button>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal && categoryToDelete} onClose={() => {
        setShowDeleteModal(false);
        setCategoryToDelete(null);
      }}>
        <div className="flex items-center mb-6">
          <AlertCircle className="h-6 w-6 text-destructive mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.deleteCategory')}</h2>
        </div>
        <div className="mb-8">
          <p className="text-card-foreground">
            {t('common.confirmDeleteCategory', { name: categoryToDelete?.name_en })}
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setShowDeleteModal(false);
              setCategoryToDelete(null);
            }}
            className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={confirmDeleteCategory}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-all duration-200"
          >
            {t('common.deleteCategory')}
          </button>
        </div>
      </Modal>

      {/* Material Details Modal */}
      {showMaterialDetails && selectedMaterial && (
        <MaterialDetails
          material={selectedMaterial}
          onClose={() => {
            setShowMaterialDetails(false);
            setSelectedMaterial(null);
          }}
        />
      )}

      {/* Material Form Modal */}
      {showMaterialForm && (
        <MaterialForm
          material={editingMaterial}
          categories={categories}
          currentCategoryId={editingMaterial?.category_id || 0}
          onSave={handleSaveMaterial}
          onCancel={() => {
            setShowMaterialForm(false);
            setEditingMaterial(null);
          }}
        />
      )}

      {/* Excel Import/Export Modal */}
      {showExcelUpload && (
        <ExcelUpload
          categories={categories}
          onUpload={handleExcelUpload}
          onCancel={() => setShowExcelUpload(false)}
          isUploading={isUploading}
        />
      )}

      {/* Category Warning Modal */}
      <Modal isOpen={showCategoryWarningModal} onClose={() => setShowCategoryWarningModal(false)}>
        <div className="flex items-center mb-6">
          <AlertCircle className="h-6 w-6 text-destructive mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.cannotDeleteCategory')}</h2>
        </div>
        <div className="mb-8">
          <p className="text-card-foreground">{categoryWarningMessage}</p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => setShowCategoryWarningModal(false)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
          >
            {t('common.ok')}
          </button>
        </div>
      </Modal>

             {/* Bulk Delete Confirmation Modal */}
      <Modal isOpen={showBulkDeleteConfirm} onClose={() => setShowBulkDeleteConfirm(false)}>
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-card-foreground mb-3">{t('common.confirmBulkDelete')}</h2>
          <p className="text-card-foreground">
            {t('common.confirmDeleteSelectedMaterials', { count: selectedMaterials.size })}
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowBulkDeleteConfirm(false)}
            className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={async () => {
              setShowBulkDeleteConfirm(false);
              if (confirmAction) {
                await confirmAction();
              }
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-all duration-200"
          >
            {t('common.deleteSelectedMaterialsCount', { count: selectedMaterials.size })}
          </button>
        </div>
      </Modal>

      {/* Category Bulk Delete Confirmation Modal */}
      <Modal isOpen={showCategoryBulkDeleteConfirm} onClose={() => setShowCategoryBulkDeleteConfirm(false)}>
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-card-foreground mb-3">{t('common.confirmBulkDelete')}</h2>
          <p className="text-card-foreground">
            {t('common.confirmDeleteSelectedCategories', { count: selectedCategories.size })}
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowCategoryBulkDeleteConfirm(false)}
            className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={async () => {
              setShowCategoryBulkDeleteConfirm(false);
              if (confirmAction) {
                await confirmAction();
              }
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-all duration-200"
          >
            {t('common.deleteSelectedCategoriesCount', { count: selectedCategories.size })}
          </button>
        </div>
      </Modal>

      {/* Material Delete Confirmation Modal */}
      <Modal isOpen={showMaterialDeleteModal && materialToDelete} onClose={() => {
        setShowMaterialDeleteModal(false);
        setMaterialToDelete(null);
      }}>
        <div className="flex items-center mb-6">
          <AlertCircle className="h-6 w-6 text-destructive mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.deleteMaterial')}</h2>
        </div>
        <div className="mb-8">
          <p className="text-card-foreground">
            {t('common.confirmDeleteMaterial', { name: materialToDelete?.name_en })}
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setShowMaterialDeleteModal(false);
              setMaterialToDelete(null);
            }}
            className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={confirmDeleteMaterial}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-all duration-200"
          >
            {t('common.deleteMaterial')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default MaterialCategories; 